import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../utils/api";

const LEVEL_STYLE = {
  gray: "bg-gray-100 text-gray-600",
  blue: "bg-blue-50 text-blue-600",
  green: "bg-green-50 text-green-700",
  purple: "bg-purple-50 text-purple-700",
  orange: "bg-orange-50 text-orange-700",
  gold: "bg-amber-50 text-amber-700",
};

function StatusPill({ done, pending, text }) {
  if (done) {
    return (
      <span className="text-xs px-2.5 py-1 rounded-full bg-green-50 text-green-700">
        {text || "已完成"}
      </span>
    );
  }
  if (pending) {
    return (
      <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">
        审核中
      </span>
    );
  }
  return (
    <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-500">
      未完成
    </span>
  );
}

export default function VerificationCenterPage() {
  const userId = localStorage.getItem("user_id");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState("");

  const [phoneCode, setPhoneCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [phonePanel, setPhonePanel] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const loadStatus = useCallback(async () => {
    if (!userId) return;
    setError("");
    try {
      const res = await apiFetch(`/api/verification/status?user_id=${userId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "加载认证状态失败");
      setStatus(data);
    } catch (err) {
      setError(err.message || "加载失败");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const postJson = async (path, body) => {
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "操作失败");
    return data;
  };

  const sendPhoneCode = async () => {
    setBusy("phone-send");
    setHint("");
    try {
      const data = await postJson("/api/verification/phone/send-code", {
        user_id: Number(userId),
      });
      if (data.already_verified) {
        setHint("手机已认证");
        await loadStatus();
        return;
      }
      setPhonePanel(true);
      setCountdown(60);
      if (data.dev_code) {
        setDevCode(data.dev_code);
        setPhoneCode(data.dev_code);
        setHint(`验证码已发送（开发模式：${data.dev_code}）`);
      } else {
        setHint(`验证码已发送至 ${data.phone_masked || "手机"}`);
      }
    } catch (err) {
      setHint(err.message || "发送失败");
    } finally {
      setBusy("");
    }
  };

  const verifyPhone = async () => {
    if (!phoneCode.trim()) {
      setHint("请填写验证码");
      return;
    }
    setBusy("phone-verify");
    setHint("");
    try {
      const data = await postJson("/api/verification/phone/verify", {
        user_id: Number(userId),
        code: phoneCode.trim(),
      });
      setHint(data.message || "手机认证成功");
      setPhonePanel(false);
      setPhoneCode("");
      setDevCode("");
      if (data.status) setStatus(data.status);
      else await loadStatus();
    } catch (err) {
      setHint(err.message || "验证失败");
    } finally {
      setBusy("");
    }
  };

  const startProvider = async (kind, simulate = "success") => {
    setBusy(kind);
    setHint("");
    try {
      const path =
        kind === "real_name"
          ? "/api/verification/real-name/start"
          : "/api/verification/face/start";
      const data = await postJson(path, {
        user_id: Number(userId),
        simulate,
        redirect_url: `${window.location.origin}/center/verify/callback?kind=${kind}&user_id=${userId}`,
      });
      if (data.status === "provider_not_configured") {
        setHint(data.message || "服务正在接入中，可先使用身份证人工审核");
        return;
      }
      if (data.status === "already_verified") {
        setHint(data.message);
        if (data.verification) setStatus(data.verification);
        return;
      }
      if (data.status === "success") {
        setHint(
          kind === "real_name"
            ? "🎉 实名认证已完成"
            : "🎉 恭喜，你已经完成真人认证！"
        );
        if (data.verification) setStatus(data.verification);
        else await loadStatus();
        return;
      }
      if (data.status === "failed") {
        setHint(data.message || "认证失败，请稍后重试");
        if (data.verification) setStatus(data.verification);
        return;
      }

      // 第三方 / 干跑：打开认证页并轮询结果
      if (data.certify_url) {
        setHint(data.message || "请在打开的页面完成认证");
        const win = window.open(data.certify_url, "_blank");
        if (!win) {
          // 弹窗被拦时同页跳转
          window.location.href = data.certify_url;
          return;
        }
        if (data.request_id) {
          await pollProviderResult(kind, data.request_id);
        } else {
          await loadStatus();
        }
        return;
      }

      setHint(data.message || "已提交，请稍后刷新查看结果");
      if (data.request_id) {
        await pollProviderResult(kind, data.request_id);
      } else {
        await loadStatus();
      }
    } catch (err) {
      setHint(err.message || "操作失败");
    } finally {
      setBusy("");
    }
  };

  const pollProviderResult = async (kind, requestId) => {
    const path =
      kind === "face"
        ? `/api/verification/face/result?user_id=${userId}&request_id=${encodeURIComponent(requestId)}`
        : `/api/verification/real-name/result?user_id=${userId}&request_id=${encodeURIComponent(requestId)}`;
    for (let i = 0; i < 15; i += 1) {
      await new Promise((r) => setTimeout(r, i === 0 ? 1500 : 2000));
      try {
        const res = await apiFetch(path);
        const data = await res.json();
        if (data.status === "success") {
          setHint(
            kind === "real_name"
              ? "🎉 实名认证已完成"
              : "🎉 恭喜，你已经完成真人认证！"
          );
          if (data.verification) setStatus(data.verification);
          else await loadStatus();
          return;
        }
        if (data.status === "failed") {
          setHint(data.message || "认证未通过");
          if (data.verification) setStatus(data.verification);
          return;
        }
        setHint(`认证处理中，请稍候…（${i + 1}/15）`);
      } catch {
        /* 继续轮询 */
      }
    }
    setHint("仍在处理中，可稍后点击「刷新认证状态」");
    await loadStatus();
  };

  const applyProfile = async () => {
    setBusy("profile");
    setHint("");
    try {
      const data = await postJson("/api/verification/profile/apply", {
        user_id: Number(userId),
      });
      setHint(data.message || "已提交资料认证");
      if (data.verification) setStatus(data.verification);
      else await loadStatus();
    } catch (err) {
      setHint(err.message || "申请失败");
    } finally {
      setBusy("");
    }
  };

  if (!userId) {
    return <div className="text-center text-gray-400 py-10">请先登录</div>;
  }

  if (loading) {
    return <div className="text-center text-gray-400 py-10">加载中...</div>;
  }

  if (error || !status) {
    return (
      <div className="text-center space-y-3 py-10">
        <p className="text-gray-500">{error || "暂无法加载认证状态"}</p>
        <button
          type="button"
          onClick={() => {
            setLoading(true);
            loadStatus();
          }}
          className="text-pink-600 text-sm"
        >
          重新加载
        </button>
      </div>
    );
  }

  const levelColor = LEVEL_STYLE[status.verification_level_color] || LEVEL_STYLE.gray;
  const realNameDone = status.real_name_verified || status.legacy_id_verified;
  const profilePending = status.profile_status === "pending";
  const profileDone =
    status.profile_verified || status.profile_status === "approved";

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <p className="text-sm text-pink-500 tracking-wide">认证中心</p>
        <h2 className="text-2xl font-bold text-gray-800">
          让真实的人，遇见真实的人
        </h2>
        <p className="text-sm text-gray-500 leading-7">
          完善认证信息，可以提高个人资料可信度，也让其他会员更放心与你交流。
        </p>
      </header>

      <section className={`rounded-2xl p-5 ${levelColor}`}>
        <p className="text-sm opacity-80">我的认证等级</p>
        <p className="text-2xl font-bold mt-1">
          {status.verification_level_label || "未认证"}
        </p>
        <div className="mt-4">
          <div className="flex justify-between text-xs mb-1 opacity-80">
            <span>认证完成度</span>
            <span>{status.completion_percent || 0}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/60 overflow-hidden">
            <div
              className="h-full rounded-full bg-current opacity-70 transition-all"
              style={{ width: `${status.completion_percent || 0}%` }}
            />
          </div>
        </div>
        {status.face_verified ? (
          <p className="text-sm mt-3 font-medium">🎉 恭喜，你已经完成真人认证！</p>
        ) : status.providers?.face_verification_enabled ? (
          <p className="text-sm mt-3 opacity-90">完成真人认证，让你的资料更可信。</p>
        ) : (
          <p className="text-sm mt-3 opacity-90">
            建议先完成手机、实名（身份证）与资料认证。
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-2">
        <h3 className="font-semibold text-gray-800">您的隐私很重要</h3>
        <p className="text-sm text-gray-600 leading-7">
          实名认证主要用于确认账号真实性。认证信息不会公开展示给其他会员。
          <br />
          平台只展示「已认证」状态，不会公开您的身份证号码、手机号等敏感信息。
          <br />
          我们优先采用第三方认证服务，尽量减少平台对敏感身份信息的保存。
        </p>
      </section>

      {hint ? (
        <div className="rounded-xl bg-green-50 text-green-800 text-sm px-4 py-3 leading-7">
          {hint}
        </div>
      ) : null}

      <div className="space-y-4">
        {/* 邮箱（注册时验证，账号级，不计入等级徽章层） */}
        <article className="rounded-2xl border bg-white p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-800">📧 邮箱认证</h3>
              <p className="text-sm text-gray-500 mt-1">
                注册时验证；用于找回密码、账号安全
              </p>
            </div>
            <StatusPill done={Boolean(status.email_verified)} />
          </div>
          {status.email_verified ? (
            <p className="text-sm text-green-700">
              ✓ 已验证{status.email ? `（${status.email}）` : ""}
            </p>
          ) : (
            <p className="text-sm text-gray-600">
              尚未绑定或验证邮箱，请到
              <Link to="/center/security" className="text-pink-600 mx-1">
                账号安全
              </Link>
              完成绑定。
            </p>
          )}
        </article>

        {/* 手机认证 */}
        <article className="rounded-2xl border bg-white p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-800">📱 手机认证</h3>
              <p className="text-sm text-gray-500 mt-1">防止虚假账号，便于找回账号（短信开通后可用）</p>
            </div>
            <StatusPill done={status.phone_verified} />
          </div>
          {status.phone_verified ? (
            <p className="text-sm text-green-700">✓ 已验证</p>
          ) : (
            <div className="space-y-3">
              {!phonePanel ? (
                <button
                  type="button"
                  disabled={busy === "phone-send"}
                  onClick={sendPhoneCode}
                  className="px-4 py-2 rounded-xl bg-blue-500 text-white text-sm disabled:opacity-50"
                >
                  {busy === "phone-send" ? "发送中..." : "立即认证"}
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <input
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value)}
                      placeholder="填写验证码"
                      className="border rounded-xl px-3 py-2 text-sm flex-1 min-w-[140px]"
                    />
                    <button
                      type="button"
                      disabled={busy === "phone-verify"}
                      onClick={verifyPhone}
                      className="px-4 py-2 rounded-xl bg-blue-500 text-white text-sm disabled:opacity-50"
                    >
                      {busy === "phone-verify" ? "验证中..." : "确认"}
                    </button>
                    <button
                      type="button"
                      disabled={countdown > 0 || busy === "phone-send"}
                      onClick={sendPhoneCode}
                      className="px-4 py-2 rounded-xl border text-sm disabled:opacity-50"
                    >
                      {countdown > 0 ? `${countdown}s` : "重发"}
                    </button>
                  </div>
                  {devCode ? (
                    <p className="text-xs text-gray-400">开发模式验证码：{devCode}</p>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </article>

        {/* 实名认证：当前主通道为身份证人工审核 */}
        <article className="rounded-2xl border bg-white p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-800">🪪 实名认证</h3>
              <p className="text-sm text-gray-500 mt-1">
                上传身份证正反面，由管理员审核（不是「资料认证」）
              </p>
            </div>
            <StatusPill
              done={realNameDone}
              pending={status.legacy_id_pending}
              text={realNameDone ? "已完成" : undefined}
            />
          </div>
          {realNameDone ? (
            <p className="text-sm text-green-700">
              ✓ 实名已验证
              {status.real_name_masked ? `（${status.real_name_masked}）` : ""}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Link
                to="/center/verify/id"
                className="px-4 py-2 rounded-xl bg-green-500 text-white text-sm hover:bg-green-600"
              >
                上传身份证（人工审核）
              </Link>
              {status.providers?.third_party_real_name_enabled ? (
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() => startProvider("real_name", "success")}
                  className="px-4 py-2 rounded-xl border border-green-500 text-green-700 text-sm disabled:opacity-50"
                >
                  {busy === "real_name" ? "处理中..." : "支付宝实名（可选）"}
                </button>
              ) : null}
            </div>
          )}
          {status.legacy_id_pending && !realNameDone ? (
            <p className="text-sm text-amber-700">身份证已上传，等待后台审核</p>
          ) : null}
          {!status.providers?.third_party_real_name_enabled ? (
            <p className="text-xs text-gray-400">
              支付宝等第三方实名暂未开放（费用较高，后台可随时开启）。请先走身份证人工审核。
            </p>
          ) : null}
        </article>

        {/* 真人认证：默认关闭 */}
        <article className="rounded-2xl border bg-white p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-800">👤 真人认证</h3>
              <p className="text-sm text-gray-500 mt-1">人脸核身，证明照片与本人一致</p>
            </div>
            <StatusPill done={status.face_verified} />
          </div>
          {status.face_verified ? (
            <p className="text-sm text-green-700">✓ 已验证</p>
          ) : status.providers?.face_verification_enabled ? (
            <button
              type="button"
              disabled={!!busy}
              onClick={() => startProvider("face", "success")}
              className="px-4 py-2 rounded-xl bg-purple-500 text-white text-sm disabled:opacity-50"
            >
              {busy === "face" ? "处理中..." : "立即认证"}
            </button>
          ) : (
            <div className="space-y-1">
              <button
                type="button"
                disabled
                className="px-4 py-2 rounded-xl bg-gray-200 text-gray-500 text-sm cursor-not-allowed"
              >
                暂未开放
              </button>
              <p className="text-xs text-gray-400">
                真人/人脸核身费用较高，已在后台保留能力，开通后这里会出现入口。
              </p>
            </div>
          )}
        </article>

        {/* 资料认证 */}
        <article className="rounded-2xl border bg-white p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-800">📋 资料认证</h3>
              <p className="text-sm text-gray-500 mt-1">
                审核个人资料是否真实完整（头像、简介等），不是上传身份证
              </p>
            </div>
            <StatusPill done={profileDone} pending={profilePending} />
          </div>
          {profileDone ? (
            <p className="text-sm text-green-700">✓ 资料已认证</p>
          ) : profilePending ? (
            <p className="text-sm text-amber-700">已提交，等待人工审核</p>
          ) : (
            <div className="space-y-2">
              <button
                type="button"
                disabled={!!busy}
                onClick={applyProfile}
                className="px-4 py-2 rounded-xl bg-orange-500 text-white text-sm disabled:opacity-50"
              >
                {busy === "profile" ? "提交中..." : "申请认证"}
              </button>
              <p className="text-xs text-gray-400">
                需先把必填资料填完整，再点申请；后台人工审核通过后才算完成。
                <Link to="/center/profile" className="text-pink-600 ml-1">
                  去完善资料
                </Link>
              </p>
            </div>
          )}
          {status.profile_status === "rejected" ? (
            <p className="text-sm text-red-600">
              未通过：{status.profile_reject_reason || "请完善后重新申请"}
            </p>
          ) : null}
        </article>

        {/* 平台认证 */}
        <article className="rounded-2xl border bg-white p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-800">🏅 平台认证</h3>
              <p className="text-sm text-gray-500 mt-1">高可信婚恋会员（管理员授予）</p>
            </div>
            <StatusPill done={status.platform_verified} />
          </div>
          {status.platform_verified ? (
            <p className="text-sm text-amber-700">✓ 已获得平台认证</p>
          ) : (
            <div className="text-sm text-gray-600 leading-7 space-y-1">
              <p>
                建议完成：手机 + 实名（身份证）+ 资料；真人认证开通后也会纳入。
                最终由管理员人工授予，不支持自助申请。
              </p>
            </div>
          )}
        </article>
      </div>

      <button
        type="button"
        onClick={loadStatus}
        className="text-sm text-gray-400 hover:text-pink-500"
      >
        刷新认证状态
      </button>
    </div>
  );
}
